import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { GET_SELLER_STORE } from "../../api/seller-store";
import { GET_PACK_ORDERS } from "../../api/pack-order-queries";
import Header from "../../../../components/common/header";
import LoadingSpinnerFull from "../../../../components/common/loading-spinner/loading-spinner-full";
import { notify, notifyError } from "../../../../utils/toast";
import PreviousOrderCard from "../sales/components/previous-order-card";
import { BLOCK_USER } from "../../../../api/blacklist";
import { HiXMark } from "react-icons/hi2";
import type { Order } from "../../../../__generated__/graphql";

const isPreviousOrder = (order: Order) => {
  const isPickupEnded =
    !!order.packPickupEndTime &&
    new Date(order.packPickupEndTime) < new Date();

  return (
    !!order.isPickupCompleted ||
    !!order.isCanceled ||
    (!!order.isPackConfirmed && isPickupEnded)
  );
};

const PreOrderPreviousSales = () => {
  const navigate = useNavigate();
  const { loading, error, data } = useQuery(GET_PACK_ORDERS, {
    fetchPolicy: "network-only",
  });
  const {
    data: storeData,
    loading: storeLoading,
    error: storeError,
  } = useQuery(GET_SELLER_STORE, { fetchPolicy: "network-only" });

  const [blockUser] = useMutation(BLOCK_USER, {
    refetchQueries: [GET_PACK_ORDERS],
  });

  const [countdown, setCountdown] = useState<string | null>(null);
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedUserName, setSelectedUserName] = useState<string>("");
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);

  const 픽업시간30분전까지남은시간 = (reserveTime: any) => {
    const now = new Date();
    const dayOfWeek = ((now.getDay() + 6) % 7).toString(); // 월요일을 0으로, 일요일을 6으로 설정
    const startTime = reserveTime?.[dayOfWeek]?.start;
    if (startTime) {
      const [hours, minutes] = startTime.split(":").map(Number);
      const startDate = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        hours,
        minutes,
      );
      startDate.setMinutes(startDate.getMinutes() - 30);
      const diff = startDate.getTime() - now.getTime();
      if (diff > 0) {
        const diffHours = Math.floor(diff / (1000 * 60 * 60));
        const diffMinutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const diffSeconds = Math.floor((diff % (1000 * 60)) / 1000);
        return `${diffHours.toString().padStart(2, "0")}:${diffMinutes
          .toString()
          .padStart(2, "0")}:${diffSeconds.toString().padStart(2, "0")}`;
      }
    }
    return null;
  };

  // countdown counter
  useEffect(() => {
    const interval = setInterval(() => {
      const timeLeft = 픽업시간30분전까지남은시간(
        storeData?.getSellerStore?.reserveTime,
      );
      setCountdown(timeLeft);
      if (countdown === "00:00:01") {
        window.location.reload();
      }
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeData]);

  if (storeLoading) return <LoadingSpinnerFull />;
  if (storeError) return <p>오류: {storeError.message}</p>;
  if (!storeData?.getSellerStore) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
        <p className="text-lg mb-6 text-gray-700">
          매장이 아직 등록되지 않았습니다.
        </p>
        <button
          onClick={() => navigate("/boss/my/store-management")}
          className="px-6 py-3 bg-blue-500 text-white font-semibold rounded-lg shadow-md hover:bg-blue-600 transition duration-300"
        >
          매장 추가하러 가기
        </button>
      </div>
    );
  }

  if (loading) return <LoadingSpinnerFull />;
  if (error) return <p>오류: {error.message}</p>;

  if (!storeData?.getSellerStore?.pack?.price) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-white">
        <p className="text-lg mb-6 text-gray-700">
          예약판매 팩 설정을 완료해주세요!⭐
        </p>
        <button
          onClick={() => navigate("/boss/pre-order/pack-management")}
          className="px-6 py-3 bg-green-500 text-white font-semibold rounded-lg shadow-md hover:bg-blue-600 transition duration-300"
        >
          예약판매 설정 완료하기
        </button>

        <img
          alt="random-pack-setting-ex"
          src={require("./../image/random-pack-setting-ex.png")}
          className="mt-4 rounded-lg w-2/3 max-w-md"
        />
        <p className="mt-2 text-md text-gray-500 text-center">
          이 두가지 설정을 완료해주시면 <br />
          소비자에게 노출됩니다!
        </p>
      </div>
    );
  }

  const previousOrders = (data?.packOrdersBySeller ?? []).filter(isPreviousOrder);
  const selectedDisplayName = selectedUserName || "익명의 히어로";

  const handleBlockClick = (orderId: number) => {
    const order = data?.packOrdersBySeller.find(
      (order: any) => order.id === orderId,
    );
    if (order?.user?.id) {
      setSelectedUserId(order.user.id);
      setSelectedUserName(order.user.name || "");
      setSelectedOrderId(orderId);
      setIsBlockModalOpen(true);
    }
  };

  const confirmBlockUser = async () => {
    if (selectedUserId !== null) {
      try {
        const result = await blockUser({
          variables: { userId: selectedUserId },
        });

        if (result.data?.blockUser?.ok) {
          notify("유저가 차단되었습니다.");
          setIsBlockModalOpen(false);
          setSelectedUserId(null);
          setSelectedUserName("");
          setSelectedOrderId(null);
        } else {
          notifyError(
            result.data?.blockUser?.error || "유저 차단에 실패했습니다.",
          );
        }
      } catch (err: any) {
        notifyError(err.message || "유저 차단에 실패했습니다.");
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#fff] flex flex-col overflow-y-scroll">
      <Header title="예약 판매 현황" showBackButton={true} />

      <div className="flex flex-col bg-white pb-10">
        <div className="p-3">
          <h3 className="text-3xl font-bold mt-4 text-gray-800">이전의 주문</h3>
          <ul className="w-full max-w-md">
            {previousOrders.length === 0 ? (
              <div className="bg-gray-100 rounded-lg p-4 mt-4">
                <p className="text-gray-600">
                  이전에 들어온 모든 랜덤팩 주문들입니다.
                </p>
              </div>
            ) : (
              <div className="bg-gray-100 rounded-lg p-4 mt-4">
                {[...previousOrders].reverse().map((order: any) => (
                  <PreviousOrderCard
                    key={order.id}
                    order={order}
                    handleReportClick={handleBlockClick}
                  />
                ))}
              </div>
            )}
          </ul>
        </div>
      </div>

      {/* 유저 차단 확인 모달 */}
      {isBlockModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-800">
                유저를 차단하시겠습니까?
              </h2>
              <button
                onClick={() => {
                  setIsBlockModalOpen(false);
                  setSelectedUserId(null);
                  setSelectedUserName("");
                  setSelectedOrderId(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <HiXMark className="w-6 h-6" />
              </button>
            </div>

            <div className="bg-gray-100 rounded-lg p-4 mb-4">
              <div className="text-xl text-gray-600 mb-1">
                이름:{" "}
                <span className="font-bold text-blue-700">
                  {selectedDisplayName}
                </span>
              </div>
              <div className="text-xl text-gray-600">
                전화번호:{" "}
                <span className="font-bold text-blue-700">
                  {data?.packOrdersBySeller.find(
                    (order: any) => order.id === selectedOrderId,
                  )?.user?.phone || "없음"}
                </span>
              </div>
            </div>

            <p className="text-lg text-gray-600 mb-2">
              <span className="font-semibold">{selectedDisplayName}</span>님을
              차단하시겠습니까?
            </p>
            <p className="text-gray-500 text-sm mb-6">
              차단된 유저는 매장이 노출되지 않으며 주문을 할 수 없습니다.
            </p>

            <div className="flex justify-end gap-3">
              <button
                className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                onClick={() => {
                  setIsBlockModalOpen(false);
                  setSelectedUserId(null);
                  setSelectedUserName("");
                  setSelectedOrderId(null);
                }}
              >
                취소
              </button>
              <button
                className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors"
                onClick={confirmBlockUser}
              >
                차단
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreOrderPreviousSales;
